import {
	ChangeDetectionStrategy,
	Component,
	inject,
	OnInit,
	signal,
} from '@angular/core';
import {
	FormControl,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
} from '@angular/forms';
import {
	DestroyService,
	DialogBase,
	DialogOptions,
} from '@context/frontend/common';
import { applyValidators } from '@context/frontend/form';
import { getFirebaseErrorKey } from '@context/shared/utils';
import { TranslocoModule } from '@jsverse/transloco';
import {
	TuiButton,
	TuiError,
	TuiIcon,
	TuiInput,
	TuiLabel,
	TuiNotification,
} from '@taiga-ui/core';
import { TuiButtonLoading, TuiPassword } from '@taiga-ui/kit';
import { FirebaseError } from 'firebase/app';
import { takeUntil } from 'rxjs';
import { AuthService } from '../../services';

@Component({
	imports: [
		TranslocoModule,
		TuiButton,
		ReactiveFormsModule,
		FormsModule,
		TuiInput,
		TuiLabel,
		TuiButtonLoading,
		TuiIcon,
		TuiPassword,
		TuiError,
		TuiNotification,
	],
	selector: 'ctx-change-password-dialog',
	templateUrl: 'change-password-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [DestroyService],
})
export class ChangePasswordDialogComponent
	extends applyValidators(DialogBase<boolean>)
	implements OnInit
{
	static override readonly DialogOptions = {
		label: 'profile.change-password-header',
		size: 's',
	} as DialogOptions;

	readonly busy = signal(false);
	readonly error = signal<string | null>(null);

	form: FormGroup | null = null;

	private readonly authService = inject(AuthService);
	private readonly destroy$ = inject(DestroyService);

	ngOnInit() {
		this.setupForm();
	}

	setupForm() {
		this.form = new FormGroup({
			currentPassword: new FormControl(null, (field) =>
				this.getRequiredValidator(
					field,
					'form.error.password.required',
				),
			),
			password: new FormControl(null, [
				(field) =>
					this.getRequiredValidator(
						field,
						'form.error.password.required',
					),
				(field) => this.getPasswordValidator(field),
			]),
		});

		this.form.addControl(
			'passwordConfirm',
			new FormControl(null, [
				(field) =>
					this.getRequiredValidator(
						field,
						'form.error.password.required',
					),
				(field) =>
					this.getMatchingValidator(
						field,
						this.form?.get('password') ?? new FormControl(null),
						'form.error.password.match',
					),
			]),
		);

		this.form
			.get('password')
			?.valueChanges.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				this.form
					?.get('passwordConfirm')
					?.updateValueAndValidity({ emitEvent: false });
			});

		this.form
			.get('passwordConfirm')
			?.valueChanges.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				this.form
					?.get('password')
					?.updateValueAndValidity({ emitEvent: false });
			});
	}

	onSubmit() {
		this.form?.markAllAsTouched();
		if (this.busy() || !this.form || this.form.invalid) return;

		const email = this.authService.user?.email;
		if (!email) {
			this.error.set('error.firebase-api');
			return;
		}

		this.form.disable();
		this.busy.set(true);
		this.error.set(null);

		const { currentPassword, password } = this.form.getRawValue();
		return this.authService
			.reauthenticateWithEmailAndPassword(email, currentPassword)
			.then(() => this.authService.updatePassword(password))
			.then(() => this.onDismiss(true))
			.catch((error: FirebaseError) => {
				this.error.set(getFirebaseErrorKey(error?.code));
				this.busy.set(false);
				this.form?.enable();
			});
	}
}
